/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";

import { ThemePalette, ThemeProps } from "@src/components/Theme";

// Ops Console buttons: primary = Deep Slate fill; secondary = white with a slate border;
// hollow = outline in the primary (or alert) colour; alert = danger fill.
const backgroundColor = (props: any) => {
  if (props.hollow) {
    if (props.transparent) {
      return "transparent";
    }
    return ThemePalette.surface;
  }
  if (props.secondary) {
    return ThemePalette.surface;
  }
  if (props.alert) {
    return ThemePalette.alert;
  }
  return ThemePalette.primary;
};
const disabledBackgroundColor = (props: any) => {
  if (props.secondary) {
    return ThemePalette.grayscale[7];
  }
  return backgroundColor(props);
};
const hoverBackgroundColor = (props: any) => {
  if (props.disabled && props.secondary) {
    return ThemePalette.grayscale[7];
  }
  if (props.secondary) {
    return ThemePalette.grayscale[1];
  }
  if (props.hoverPrimary || props.hollow) {
    return props.alert ? ThemePalette.alert : ThemePalette.primary;
  }
  if (props.alert) {
    return "color-mix(in srgb, var(--t9-danger-700) 85%, black)"; // was #A93226
  }
  return ThemePalette.primaryLight;
};
const border = (props: any) => {
  if (props.secondary) {
    return `border: 1px solid ${ThemePalette.grayscale[2]};`;
  }
  if (props.hollow) {
    if (props.alert) {
      return `border: 1px solid ${ThemePalette.alert};`;
    }
    return `border: 1px solid ${ThemePalette.primary};`;
  }
  return "";
};
const disabledBorder = (props: any) => border(props);
const color = (props: any) => {
  if (props.secondary) {
    return props.disabled ? ThemePalette.grayscale[3] : ThemePalette.black;
  }
  if (props.hollow) {
    if (props.alert) {
      return ThemePalette.alert;
    }
    return ThemePalette.primary;
  }
  return ThemePalette.onFill;
};
const getWidth = (props: any) => {
  if (props.width) {
    return props.width;
  }

  if (props.large) {
    return `${ThemeProps.inputSizes.large.width}px`;
  }
  return `${ThemeProps.inputSizes.regular.width}px`;
};
const StyledButton = styled.button`
  ${ThemeProps.exactHeight("34px")}
  border-radius: 6px;
  font-family: inherit;
  font-weight: 600;
  margin: 0;
  background-color: ${props => backgroundColor(props)};
  border: none;
  ${props => border(props)}
  color: ${props => color(props)};
  padding: 0;
  ${props => ThemeProps.exactWidth(getWidth(props))}
  cursor: pointer;
  font-size: 14px;
  transition:
    background-color ${ThemeProps.animations.swift},
    opacity ${ThemeProps.animations.swift};
  &:disabled {
    opacity: ${(props: any) => (props.secondary ? 1 : 0.7)};
    cursor: not-allowed;
    background-color: ${props => disabledBackgroundColor(props)};
    ${props => disabledBorder(props)}
  }
  &:hover {
    ${(props: any) =>
      props.hollow
        ? `color: ${props.disabled ? ThemePalette.grayscale[3] : props.secondary ? ThemePalette.black : "white"};`
        : ""}
    background-color: ${props => hoverBackgroundColor(props)};
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px ${ThemePalette.accent};
  }
`;

const Button = (props: any) => (
  <StyledButton
    {...props}
    onFocus={e => {
      e.target.blur();
    }}
  />
);

export default Button;
